import { Component, ComponentRef, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { InteractionService } from '../../../services/interaction.service';
import { SynchronizationService } from '../../../services/synchronization.service';
import { SettingsService } from '../../../services/settings.service';
import { Subscription } from 'rxjs';
import { RatingService } from '../../../services/rating.service';
import { Vote } from '../../../models/vote';
import { IWidget } from '../../../interfaces/iWidget';

export enum teamMoodsViews {
  smallView = 'smallView',
  showAvg = 'showAvg',
  showTrends = 'showTrends',
  voting = 'voting',
  editQuestion = 'editQuestion',
  showTimeRange = 'showTimeRange',
  showFeedback = 'showFeedback'
}


@Component({
  selector: 'app-team-mood',
  templateUrl: './team-mood.component.html',
  styleUrls: ['./team-mood.component.scss']
  })

export class TeamMoodComponent implements IWidget, OnInit, OnDestroy {
  static widgetName = 'teamMood';
  private draggable: boolean;
  @ViewChild('divcontainer', { static: true }) private element: ElementRef;
  languages: string[];
  private interactedClass = 'resizedwidget';
  private event: Subscription[] = [];
  public view: teamMoodsViews = teamMoodsViews.smallView;
  public lastVisitedView: string;
  public projectKey: string;
  public actualTeamMood: number;

  // style
  public zIndex = 25;


  // ref
  public componentRef: ComponentRef<any>;

  set isUndocked(isUndocked: boolean) {
    if (isUndocked) {
      this.showUndockedVersion();
    } else {
      this.showDockedVersion();
    }
  }

  constructor(private translate: TranslateService,
    private ratingService: RatingService,
    private interactionService: InteractionService,
    private synchronization: SynchronizationService,
    private settingsService: SettingsService) {
    this.languages = this.translate.getLangs();
  }

  ngOnInit() {
    this.event.push(this.settingsService.getUpdateObservable().subscribe(() => {
      if (this.element.nativeElement.classList.contains(this.interactedClass)) {
        this.resetWidget(this.element.nativeElement);
      }
      this.projectKey = this.settingsService.getProjectName();
      const sprintId = this.settingsService.getSprintId(this.projectKey);
      this.ratingService.setSelectedSprintId(sprintId.toString());
      this.ratingService.emitUpdateSelectedProps(true);

      this.loadData();
    }));
    this.loadData();
    this.setupNewVoteObservable();
    this.setupRealTimeEvents();
  }

  private setupRealTimeEvents() {
    const obj = this;
    this.synchronization.subscribeEvent<any>('teamMoodResizeWidget')
      .subscribe(size => {
        const target = obj.element.nativeElement;
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
      });
  }

  public getName() {
    return TeamMoodComponent.widgetName;
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
    // Cleanup interactjs-listeners
    this.interactionService.unset('.teamMoodWidget');
    this.RemoveLocalStorage();
  }

  loadData() {
    this.projectKey = this.settingsService.getProjectName();
    this.loadObservable();
  }

  setupNewVoteObservable() {
    this.ratingService.newVoteObservable.subscribe(() => {
      this.loadObservable();
    });
  }

  loadObservable() {
    const sprintId = this.settingsService.getSprintId(this.projectKey);

    this.ratingService.getAvgSprint(this.settingsService.getProjectName(), sprintId.toString()).subscribe(res => {
      this.calculateTeamMood(res);
    });
  }

  calculateTeamMood(res: any): void {
    this.actualTeamMood = 0;
    if (res instanceof Array) {
      let countEnteredQuestions = 0;
      for (const q of res.filter(x => x['avg'] > 0)) {
        this.actualTeamMood += q['avg'];
        countEnteredQuestions++;
      }
      if (countEnteredQuestions !== 0) {
        this.actualTeamMood = Number((this.actualTeamMood / countEnteredQuestions).toFixed(2));
      }
    }
  }

  onViewChange(newView: teamMoodsViews) {
    this.lastVisitedView = this.view;
    this.view = newView;
  }

  goToLastView() {
    this.view = teamMoodsViews[this.lastVisitedView];
    this.lastVisitedView = null;
  }

  editQuestions() {
    this.lastVisitedView = this.view;
    this.view = teamMoodsViews.editQuestion;
  }

  private showDockedVersion() {
    this.view = teamMoodsViews.smallView;

    const element = document.getElementById('widgetId');
    element.classList.remove('un-docked');
    element.classList.add('docked');
  }

  private showUndockedVersion() {
    this.RemoveLocalStorage();

    const element = document.getElementById('widgetId');
    element.classList.remove('docked');
    element.classList.add('un-docked');
    this.view = teamMoodsViews.showAvg;
  }

  private RemoveLocalStorage() {
    localStorage.removeItem('teamMoodEndDate');
    localStorage.removeItem('teamMoodStartDate');
  }

  reset(event: any) {
    this.synchronization.sendRealTimeEvent(TeamMoodComponent.widgetName + 'ResetWidget');
    const closest = event.target.closest(`.${this.interactedClass}`);
    this.resetWidget(closest);
  }

  resetWidget(target: HTMLElement) {
    this.interactionService.undoDragInteraction(target);
    this.interactionService.undoPinchToResizeInteraction(target);
    target.classList.remove(this.interactedClass);
    this.view = teamMoodsViews.smallView;
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }
}
